import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import MetricsCard from './components/MetricsCard';
import ResolutionTimeChart from './components/ResolutionTimeChart';
import TeamPerformanceChart from './components/TeamPerformanceChart';
import RootCauseChart from './components/RootCauseChart';
import FilterPanel from './components/FilterPanel';
import ComplianceReport from './components/ComplianceReport';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const ReportsAnalytics = () => {
  const [filters, setFilters] = useState({
    timeRange: 'monthly',
    severity: 'all',
    status: 'all',
    team: 'all',
    startDate: '',
    endDate: ''
  });

  const [isExporting, setIsExporting] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Mock user data
  const mockUser = {
    id: 1,
    name: "Sarah Chen",
    email: "sarah.chen@cyberguard.gov",
    role: "Senior Analyst",
    permissions: ["view_dashboard", "view_incidents", "view_reports", "create_incident"]
  };

  // Mock incident counts
  const mockIncidentCounts = {
    critical: 3,
    high: 8,
    medium: 15,
    low: 24
  };

  // Mock metrics data
  const mockMetrics = [
    {
      title: "Average Resolution Time",
      value: "4.2h",
      change: "-12%",
      changeType: "positive",
      icon: "Clock",
      description: "vs last month",
      trend: [5.2, 4.8, 4.5, 4.3, 4.1, 4.0, 4.2]
    },
    {
      title: "SLA Compliance",
      value: "94.2%",
      change: "+2.1%",
      changeType: "positive",
      icon: "Target",
      description: "Target: 95%",
      trend: [92.1, 92.8, 93.2, 93.8, 94.1, 94.0, 94.2]
    },
    {
      title: "Total Incidents",
      value: "156",
      change: "+8%",
      changeType: "negative",
      icon: "AlertTriangle",
      description: "This month",
      trend: [120, 135, 142, 148, 152, 154, 156]
    },
    {
      title: "Team Efficiency",
      value: "87.5%",
      change: "+5.2%",
      changeType: "positive",
      icon: "Users",
      description: "Productivity score",
      trend: [82.1, 83.5, 85.2, 86.1, 86.8, 87.2, 87.5]
    }
  ];

  // Mock resolution time data
  const mockResolutionData = [
    { date: "2025-01-01", avgResolutionTime: 5.2, slaCompliance: 92.1 },
    { date: "2025-01-08", avgResolutionTime: 4.8, slaCompliance: 92.8 },
    { date: "2025-01-15", avgResolutionTime: 4.5, slaCompliance: 93.2 },
    { date: "2025-01-22", avgResolutionTime: 4.3, slaCompliance: 93.8 },
    { date: "2025-01-29", avgResolutionTime: 4.1, slaCompliance: 94.1 },
    { date: "2025-02-05", avgResolutionTime: 4.0, slaCompliance: 94.0 },
    { date: "2025-02-12", avgResolutionTime: 4.2, slaCompliance: 94.2 }
  ];

  // Mock team performance data
  const mockTeamData = [
    { name: "Sarah Chen", resolved: 28, assigned: 32, avgTime: 3.8, workload: 87 },
    { name: "Mike Rodriguez", resolved: 24, assigned: 28, avgTime: 4.2, workload: 85 },
    { name: "Emily Watson", resolved: 31, assigned: 35, avgTime: 3.5, workload: 88 },
    { name: "David Kim", resolved: 19, assigned: 25, avgTime: 5.1, workload: 76 },
    { name: "Lisa Thompson", resolved: 26, assigned: 30, avgTime: 4.0, workload: 86 },
    { name: "James Wilson", resolved: 22, assigned: 27, avgTime: 4.5, workload: 81 }
  ];

  // Mock root cause data
  const mockRootCauseData = [
    { name: "Malware/Ransomware", value: 42 },
    { name: "Phishing Attacks", value: 38 },
    { name: "Unauthorized Access", value: 24 },
    { name: "Data Breach", value: 18 },
    { name: "DDoS Attacks", value: 15 },
    { name: "Insider Threats", value: 12 },
    { name: "System Vulnerabilities", value: 7 }
  ];

  // Mock compliance data
  const mockComplianceData = [
    {
      category: "Response Time SLA",
      percentage: 94.2,
      target: 95,
      total: 156,
      compliant: 147,
      description: "Initial response within 1 hour"
    },
    {
      category: "Resolution Time SLA",
      percentage: 89.1,
      target: 90,
      total: 156,
      compliant: 139,
      description: "Resolution within defined timeframes"
    },
    {
      category: "Documentation Compliance",
      percentage: 97.4,
      target: 98,
      total: 156,
      compliant: 152,
      description: "Complete incident documentation"
    },
    {
      category: "Escalation Procedures",
      percentage: 91.7,
      target: 95,
      total: 156,
      compliant: 143,
      description: "Proper escalation protocols followed"
    },
    {
      category: "Communication Standards",
      percentage: 95.5,
      target: 95,
      total: 156,
      compliant: 149,
      description: "Stakeholder notification requirements"
    },
    {
      category: "Audit Trail Integrity",
      percentage: 99.4,
      target: 99,
      total: 156,
      compliant: 155,
      description: "Complete audit trail maintenance"
    }
  ];

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleExport = async (format) => {
    setIsExporting(true);
    
    // Simulate export process
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Mock export logic
    console.log(`Exporting report in ${format} format with filters:`, filters);
    
    // In a real application, this would trigger a download
    const exportData = {
      format,
      filters,
      timestamp: new Date()?.toISOString(),
      data: {
        metrics: mockMetrics,
        resolutionTimes: mockResolutionData,
        teamPerformance: mockTeamData,
        rootCauses: mockRootCauseData,
        compliance: mockComplianceData
      }
    };
    
    // Create a mock download
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cyberguard-report-${format}-${Date.now()}.json`;
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    URL.revokeObjectURL(url);
    
    setIsExporting(false);
  };

  const handleResetFilters = () => {
    setFilters({
      timeRange: 'monthly',
      severity: 'all',
      status: 'all',
      team: 'all',
      startDate: '',
      endDate: ''
    });
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'BarChart3' },
    { id: 'performance', label: 'Performance', icon: 'TrendingUp' },
    { id: 'compliance', label: 'Compliance', icon: 'Shield' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header user={mockUser} incidentCounts={mockIncidentCounts} />
      <main className="nav-offset">
        <div className="max-w-full px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb />
          
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-semibold text-foreground">Reports & Analytics</h1>
              <p className="text-muted-foreground mt-2">
                Comprehensive incident analysis and team performance insights for cybersecurity management
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                iconName="RefreshCw"
                iconPosition="left"
                onClick={() => window.location?.reload()}
              >
                Refresh Data
              </Button>
              <Button
                variant="default"
                iconName="Calendar"
                iconPosition="left"
                onClick={() => console.log('Schedule report')}
              >
                Schedule Report
              </Button>
            </div>
          </div>

          <FilterPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            onExport={handleExport}
            onReset={handleResetFilters}
            isExporting={isExporting}
          />

          {/* Tab Navigation */}
          <div className="flex items-center space-x-1 mb-6 border-b border-border">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors duration-200 ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border-active'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="space-y-8">
              {/* Key Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {mockMetrics?.map((metric, index) => (
                  <MetricsCard key={index} {...metric} />
                ))}
              </div>

              {/* Charts Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <ResolutionTimeChart data={mockResolutionData} timeRange={filters?.timeRange} />
                <RootCauseChart data={mockRootCauseData} />
              </div>
            </div>
          )}

          {activeTab === 'performance' && (
            <div className="space-y-8">
              {/* Performance Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <MetricsCard
                  title="Team Productivity"
                  value="87.5%"
                  change="+5.2%"
                  changeType="positive"
                  icon="Users"
                  description="Overall efficiency"
                />
                <MetricsCard
                  title="Avg Response Time"
                  value="23min"
                  change="-8min"
                  changeType="positive"
                  icon="Clock"
                  description="First response"
                />
                <MetricsCard
                  title="Workload Balance"
                  value="84.2%"
                  change="+2.1%"
                  changeType="positive"
                  icon="Scale"
                  description="Distribution score"
                />
              </div>

              {/* Team Performance Chart */}
              <TeamPerformanceChart data={mockTeamData} metric="resolved" />

              {/* Resolution Time Trend */}
              <ResolutionTimeChart data={mockResolutionData} timeRange={filters?.timeRange} />
            </div>
          )}

          {activeTab === 'compliance' && (
            <div className="space-y-8">
              {/* Compliance Overview */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <MetricsCard
                  title="Overall Compliance"
                  value="94.2%"
                  change="+1.8%"
                  changeType="positive"
                  icon="Shield"
                  description="Weighted average"
                />
                <MetricsCard
                  title="Audit Readiness"
                  value="97.4%"
                  change="+0.5%"
                  changeType="positive"
                  icon="FileCheck"
                  description="Documentation complete"
                />
                <MetricsCard
                  title="SLA Breaches"
                  value="9"
                  change="-3"
                  changeType="positive"
                  icon="AlertTriangle"
                  description="This month"
                />
              </div>

              {/* Compliance Dashboard */}
              <ComplianceReport data={mockComplianceData} />
            </div>
          )}

          {/* Export Status */}
          {isExporting && (
            <div className="fixed bottom-4 right-4 bg-card border border-border rounded-lg p-4 shadow-medium">
              <div className="flex items-center space-x-3">
                <div className="animate-spin">
                  <Icon name="Loader2" size={20} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Generating Report</p>
                  <p className="text-xs text-muted-foreground">Please wait while we prepare your export...</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ReportsAnalytics;